import React, { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { FileMetadata, getDefinition, MediaType } from './model';
import { ArrowLeft1, ArrowRight1 } from '@myua/icons/dist/bold24';
import { Share } from '@myua/icons/dist/linear24';
import copyToClipboard from 'copy-to-clipboard';
import './fileViewer.scss';
import { Media } from './Media';
import { LinkOutlined, DownloadOutlined, CloseOutlined, QrcodeOutlined } from '@ant-design/icons';
import { LayerToolOptions } from './viewers/common/layer/layerTool';
import { CompressedFileTreeProviderOptions } from './viewers/common/compressed';
import { ImageMetadataProviderOptions } from './viewers/common/image';
import QRCode from 'qrcode-svg';
import { Button, Popover } from '@myua/kit';
import { GDButton } from '../../../creativeHub/modules/downloader/components/DownloadButton';
import { FigmaButtonRaw } from '../../../creativeHub/modules/figmaImport/FigmaButton';
import { NodeLabel } from '../../node';

const QRButton: React.FC<{ request: () => Promise<string> }> = ({ request }) => {
  const [link, setLink] = useState<{ url: string } | null | 'loading'>(null);
  const [open, setOpen] = useState(false);
  const qrcodeSvg = useMemo(() => {
    if (link === 'loading' || link === null) {
      return null;
    }
    return new QRCode({
      content: link.url,
      container: 'svg', //Responsive use
      join: true, //Crisp rendering and 4-5x reduced file size
      width: 200,
      height: 200,
      padding: 0,
    }).svg();
  }, [link]);

  useEffect(() => {
    async function trigger() {
      try {
        setLink('loading');
        const link = await request();
        setLink({ url: link });
      } catch {
        setOpen(false);
        setLink(null);
      }
    }

    if (open === false || link !== null) return;
    trigger();
  }, [open, link, request]);

  // Semantic держал два стейта (текст подписи + цвет кнопки), хотя менялись они
  // всегда вместе — свели к одному флагу.
  const [copied, setCopied] = useState(false);
  return (
    <Popover
      trigger="click"
      open={open}
      placement="bottomRight"
      onOpenChange={setOpen}
      content={
        <div>
          <div
            style={{ height: 200, width: 200, marginBottom: 16 }}
            dangerouslySetInnerHTML={qrcodeSvg ? { __html: qrcodeSvg } : undefined}
          ></div>
          <Button
            block
            type={copied ? 'primary' : 'default'}
            disabled={link === null || link === 'loading'}
            onClick={() => {
              if (link === null || link === 'loading') return;
              copyToClipboard(link.url);
              setCopied(true);
            }}
            onMouseLeave={() => setCopied(false)}
          >
            {copied ? 'Copied' : 'Copy direct link'}
          </Button>
        </div>
      }
    >
      <button className={`ch-file-viewer__btn gen-file-viewer__btn_top ch-file-viewer__btn_copy`}>
        <QrcodeOutlined />
      </button>
    </Popover>
  );
};

const CopyLinkButton: React.FC<{
  copyLink?: string;
  mediaType: MediaType;
  quickShareProvider: { request: () => Promise<string> } | null;
}> = ({ copyLink, mediaType, quickShareProvider }) => {
  const [hint, setHint] = useState('');
  return (
    <>
      <div className="ch-file-viewer__hint">{hint}</div>
      <button
        className={`ch-file-viewer__btn gen-file-viewer__btn_top ch-file-viewer__btn_copy  ${
          copyLink ? '' : 'loading'
        }`}
        title={'Copy'}
        disabled={!copyLink}
        onClick={() => {
          if (!copyLink) return;
          copyToClipboard(copyLink);
          setHint('Copied');
        }}
        onMouseLeave={() => setHint('')}
      >
        <LinkOutlined />
      </button>
      {mediaType === MediaType.IFrame && quickShareProvider ? (
        <QRButton request={quickShareProvider.request} />
      ) : null}
    </>
  );
};
export const formatDuration = (rawSeconds: number) => {
  const hours = Math.floor(rawSeconds / 3600);
  const minutes = Math.floor(rawSeconds / 60) % 60;

  const seconds_x100_Round = Math.round((rawSeconds % 60) * 100);

  const secondsWhole = Math.floor(seconds_x100_Round / 100);
  const subSeconds = seconds_x100_Round % 100;

  const subSecondsString = subSeconds.toString(10).padStart(2, '0');
  const secondsString = secondsWhole.toString().padStart(2, '0');
  const minutesString = minutes.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${minutesString}:${secondsString}`;
  }
  return (
    <span className="ch-file-viewer__media-duration">
      {minutesString}:{secondsString}
      <span className="ch-file-viewer__media-duration-subseconds">.{subSecondsString}</span>
    </span>
  );
};
export const FileViewer: React.FC<{
  onNext: (() => void) | undefined;
  onPrev: (() => void) | undefined;
  onClose: () => void;
  onDownloadClicked?: () => void;

  copyLink?: string;
  filename: string;
  url: string;
  disableKeyboard?: boolean;
  copyLinkEnabled?: boolean;

  // если существует, отображать кнопку шеринга и это -- коллбек на ее onClick
  shareAssetButtonCallback: (() => void) | null;
  // если шеринг в прогрессе, засеривать и дизейблить кнопку шера
  shareAssetInProgress: boolean;
  // если коллбек шеринга null, но задан этот текст -- показывать кнопку шеринга
  // задизейбленной с этим тултипом (демо-стенд, MYUA-16201)
  shareAssetButtonDisabledTooltip?: string;
  description?: JSX.Element;
  muted?: boolean;
  position?: JSX.Element;
  layerTool?: LayerToolOptions;
  compressedFileTreeProvider: CompressedFileTreeProviderOptions | null;
  quickShareProvider: { request: () => Promise<string> } | null;
  imageMetadataProvider: ImageMetadataProviderOptions | null;
  googleDriveProvider: { url: string | null } | null;
  figmaImportProvider: { node: NodeLabel | null } | null;
  mediaType?: MediaType;
  mediaContent?: ReactNode;
  // extra content below
  footerContent?: JSX.Element | null;
  headerExtra?: ReactNode;
  // Extra icon button(s) rendered inside the top-right toolbar, before the
  // built-in share / download / close actions.
  toolbarExtra?: ReactNode;
}> = (props) => {
  props = { ...props };
  props.disableKeyboard ??= false;
  props.mediaType ??= getDefinition(props.filename).mt;
  props.footerContent ??= null;

  const [meta, setMeta] = useState<FileMetadata | null>(null);

  useEffect(() => {
    setMeta(null);
  }, [props.url]);

  const onKeyDown = useCallback(
    ({ key }: KeyboardEvent) => {
      if (props.disableKeyboard) return;
      if (key === 'ArrowLeft') {
        props.onPrev?.();
      }
      if (key === 'ArrowRight') {
        props.onNext?.();
      }
    },
    // eslint incorrectly asks `props` to be included into deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onNext, props.onPrev, props.disableKeyboard],
  );

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  return (
    <div className="ch-file-viewer__grid">
      <div className="ch-file-viewer__top">
        <div className="ch-file-viewer__file-info">
          {props.mediaType !== MediaType.UNKNOWN && (
            <div className="gen-file-viewer__file-info-line gen-file-viewer__file-info-line-tight">
              {props.position && (
                <>
                  <span>{props.position} </span> <span>&sdot;</span>
                </>
              )}
              {props.filename}
              {props.description && (
                <>
                  <span>&sdot;</span>
                  <span>{props.description} </span>
                </>
              )}
            </div>
          )}
          {meta && (
            <div className="gen-file-viewer__file-info-line">
              {meta.width !== undefined && meta.height !== undefined && (
                <span className="ch-file-viewer__file-info-resolution">
                  {meta.width} &times; {meta.height}
                </span>
              )}
              {meta.duration !== undefined && formatDuration(meta.duration)}
            </div>
          )}
        </div>
        <div className="ch-file-viewer__header-extra">{props.headerExtra}</div>
        <div className="ch-file-viewer__context-buttons">
          {props.toolbarExtra}
          {props.copyLinkEnabled ? (
            <CopyLinkButton
              copyLink={props.copyLink}
              mediaType={props.mediaType}
              quickShareProvider={props.quickShareProvider}
            />
          ) : null}
          {props.shareAssetButtonCallback !== null ? (
            <button
              className={`ch-file-viewer__btn gen-file-viewer__btn_top ch-file-viewer__btn_share ${
                props.shareAssetInProgress ? 'loading' : ''
              }`}
              title="Share"
              disabled={props.shareAssetInProgress}
              onClick={props.shareAssetButtonCallback}
            >
              <Share />
            </button>
          ) : props.shareAssetButtonDisabledTooltip ? (
            <button
              className="ch-file-viewer__btn gen-file-viewer__btn_top ch-file-viewer__btn_share"
              title={props.shareAssetButtonDisabledTooltip}
              disabled
            >
              <Share />
            </button>
          ) : null}

          {props.onDownloadClicked && (
            <>
              <button
                className="ch-file-viewer__btn gen-file-viewer__btn_top"
                onClick={props.onDownloadClicked}
                title={'Download'}
              >
                <DownloadOutlined />
              </button>
            </>
          )}
          {props.googleDriveProvider && props.googleDriveProvider.url !== null && (
            <GDButton
              url={props.googleDriveProvider.url}
              className="ch-file-viewer__btn gen-file-viewer__btn_top"
              title={'Open on Google Drive'}
            />
          )}
          {props.figmaImportProvider && (
            <FigmaButtonRaw
              nodes={props.figmaImportProvider.node ? [props.figmaImportProvider.node] : null}
              className="ch-file-viewer__btn gen-file-viewer__btn_top"
              title={'Import layers from Figma'}
            />
          )}
          <div
            className="ch-file-viewer__btn gen-file-viewer__btn_top"
            title={'Close'}
            onClick={props.onClose}
          >
            <CloseOutlined />
          </div>
        </div>
      </div>

      {props.onPrev && (
        <div
          className="ch-file-viewer__btn ch-file-viewer__btn_change-page prev"
          title={'Previous'}
          onClick={props.onPrev}
        >
          <ArrowLeft1 />
        </div>
      )}

      {props.mediaContent ?? (
        <Media
          filename={props.filename}
          src={props.url}
          type={props.mediaType}
          onMetadataAvailable={setMeta}
          attachEventsToDocument
          autoPlay
          muted={props.muted}
          layerTool={props.layerTool}
          compressedFileTreeProvider={props.compressedFileTreeProvider}
          imageMetadataProvider={props.imageMetadataProvider}
        />
      )}

      {props.onNext && (
        <div
          className="ch-file-viewer__btn ch-file-viewer__btn_change-page next"
          title={'Next'}
          onClick={props.onNext}
        >
          <ArrowRight1 />
        </div>
      )}
      <div className="ch-file-viewer__bottom">{props.footerContent}</div>
    </div>
  );
};
